import React, { useState, useEffect, useCallback } from 'react';
import styled from './index.module.scss';
import { useQuestionDispatch, useQuestionStore, QuestionProvider } from './QuestionProvider';
import QuestionBanner from './Banner';
import Answer from './Examination';
import Finshed from './Examination/Finshed';
import { useGlobal } from '@/utils/useGlobal';
import EfModal from '@/components/UI/Modal';
import Button from '@/components/UI/Button';
import wss from '@/utils/mqtt-client';
import { getWSSUrl } from '@/utils/countryRegion';
import topics from '@/utils/mqtt-client/crowd-funding-topics';
import { QStatusEnum } from '@/components/AppreciationDay/index.d';
import { loginUser } from '@/utils/cookie';
import { signupCookie } from '@/utils/cookies/signupCookie';

interface QuestionsProps {
    isLogin: boolean;
    setShowPage: (page: string) => void;
    refershData: () => void;
    scenario?: string;
}

const Questions: React.FC<QuestionsProps> = ({ isLogin, setShowPage, refershData, scenario }) => {
    const { useLang, useRouter, getSystemTime } = useGlobal();
    const { locale, asPath, route } = useRouter();
    const { t } = useLang('appreciationday');
    const { questionInfo, question, loading, scenario: scn } = useQuestionStore();
    const dispatch = useQuestionDispatch();
    const [showExam, setShowExam] = useState(false);
    const [showFinshed, setShowFinshed] = useState(false);
    const [showRules, setShowRules] = useState(false);
    const time = getSystemTime();
    const url = String(getWSSUrl(locale));
    const userId = loginUser()?.user?.userId;

    const { receiver, mqttUnsubscribe, mqttSubscribe } = wss({
        url,
        topics,
        locale,
        subscribes: ['updatePoints'],
        activityId: userId,
    });

    useEffect(() => {
        if (!receiver || !receiver.data) return;
        const { questionReward } = receiver.data;
        refershData();
        dispatch({
            type: 'changeReward',
            payload: {
                rewardCouponCount: questionReward.rewardCouponCount,
                rewardTotalPoints: questionReward.rewardTotalPoints,
            },
        });
    }, [receiver]);

    const handleBegin = () => {
        if (!isLogin) {
            signupCookie?.({
                sector_name: btnText?.(),
                entry_position: 0,
            });
            location.href = `/${locale}/login?redirect=${encodeURIComponent(`/${locale}/${asPath}#Question`)}`;
            return;
        }
        // 没有活动信息
        if (!questionInfo) return;
        const { questionGroupId, campaignId } = questionInfo;
        dispatch({
            type: 'getQuestionList',
            payload: {
                campaignId,
                questionGroupId,
                locale,
                url: `/${locale}${route}`,
            },
        });
    };

    const handleClose = () => {
        dispatch({
            type: 'getQuestionInfo',
            payload: { url: `/${locale}${route}`, locale },
        });
        setShowExam(false);
    };

    const handleLookBack = () => {
        dispatch({
            type: 'changeIndex',
            payload: 0,
        });
        setShowFinshed(false);
    };

    const handleOpenHistory = () => {
        if (!isLogin) {
            signupCookie?.({
                sector_name: t('question.view_log'),
                entry_position: 0,
            });
            location.href = `/${locale}/login?redirect=${encodeURIComponent(`/${locale}/${asPath}#Question`)}`;
            return;
        }
        setShowPage('qhistory_modal');
    };

    const btnText = useCallback(() => {
        if (!isLogin && questionInfo) {
            if (time < questionInfo.campaignStartTime || time > questionInfo.campaignEndTime) {
                return t('question.no_start');
            }
            return t('question.start');
        }
        switch (questionInfo?.answerStatus) {
            case QStatusEnum.FIRST:
            case QStatusEnum.NEXT:
                return t('question.start');
            case QStatusEnum.PROCEED:
                return t('question.next');
            case QStatusEnum.FINISH:
                return t('question.finshed');
            default:
                return t('question.no_start');
        }
    }, [questionInfo]);

    useEffect(() => {
        // 试题为空
        if (!question || question.length === 0) return;
        // 已开始作答
        if (showExam) return;
        // 判断当前作答序号
        let ansIndex = 0;
        for (const item of question) {
            if (item.defAnswer === '') break;
            ansIndex++;
        }
        // 已作答结束
        if (ansIndex >= question.length) {
            setShowFinshed(true);
        } else {
            dispatch({
                type: 'changeIndex',
                payload: ansIndex,
            });
        }
        setShowExam(true);
    }, [question]);

    useEffect(() => {
        dispatch({
            type: 'getQuestionInfo',
            payload: { url: `/${locale}${route}`, locale },
        });
        dispatch({
            type: 'changeScenario',
            payload: scenario ? scenario : '',
        });
        if (isLogin) mqttSubscribe('updatePoints');
        return () => mqttUnsubscribe('updatePoints');
    }, []);

    if (!questionInfo) return <div id="Question"></div>;

    return (
        <div className={`${styled.questions} ${scn ? scn : ''}`} id="Question">
            <QuestionBanner showRules={() => setShowRules(true)} />
            {/* 答题弹窗 */}
            <Button
                type="primary"
                className="textbtn"
                disabled={
                    time < questionInfo.campaignStartTime || time > questionInfo.campaignEndTime || (isLogin && questionInfo?.answerStatus === QStatusEnum.NONE)
                }
                aria-label={''}
                onClick={handleBegin}
                loading={loading}
            >
                {btnText()}
            </Button>
            <Button type="link" className="historybtn" onClick={handleOpenHistory}>
                {t('question.view_log')} &gt;
            </Button>
            <EfModal className={`${styled.question_modal}`} open={showExam} visible={showExam} width={960} onCancel={handleClose} zIndex={999}>
                {!showFinshed ? <Answer finshed={() => setShowFinshed(true)} /> : <Finshed lookBack={handleLookBack} />}
            </EfModal>
            {/* rules弹窗 */}
            <EfModal className={styled.rules_modal} open={showRules} visible={showRules} width={960} zIndex={1200} onCancel={() => setShowRules(false)}>
                <div className="rule-content">
                    <h5 className="rule-title">{t('question.rules')}</h5>
                    <div className="rule-item">{questionInfo.description}</div>
                </div>
            </EfModal>
        </div>
    );
};

const HQuestion: React.FC<QuestionsProps> = (props) => (
    <QuestionProvider>
        <Questions {...props} />
    </QuestionProvider>
);

export default HQuestion;
